import {loadStripe} from '@stripe/stripe-js';
import {$qsAll} from '../../../../@shared/ts/dom';
import {FeatureFlag} from '../../../../@type/features';
import {type PaymentData} from '../../../../@type/stripe';
import {type IStripeContext, type StripeGateway} from '../../models/stripe';
import {Feature, startModalProcessing, stopModalLoading} from '../../reducers/environmentReducer';
import {PaymentConfiguration, registerGatewayBatch} from '../../reducers/paymentConfigurationReducer';
import {store} from '../../store';
import {setupStripeButton} from './button';
import {initStripeACHDebitMethod} from './gateways/achDebit';
import {initStripeAffirmMethod} from './gateways/affirm';
import {initStripeAfterPayMethod} from './gateways/afterpay';
import {initStripeApplepayMethod} from './gateways/applepay';
import {initStripeBancontactMethod} from './gateways/bancontact';
import {initStripeCardMethod} from './gateways/card';
import {initStripeEPSMethod} from './gateways/eps';
import {initStripeGiropayMethod} from './gateways/giropay';
import {initStripeGooglepayMethod} from './gateways/googlepay';
import {initStripeIdealMethod} from './gateways/ideal';
import {initStripeKlarnaMethod} from './gateways/klarna';
import {initStripeP24Method} from './gateways/p24';
import {initStripeSepaDebitMethod} from './gateways/sepaDebit';
import {initStripeSofortMethod} from './gateways/sofort';
import {displayPaymentErrorMessage, requestCartCalculation, type OrderService} from '../order';
import {type GatewayConfiguration} from '../../models/GatewayConfiguration';
import {getErrorString} from '../../../../@shared/ts/error';
import {getLocaleText} from '../../util/translation';
import {PeachPayOrder} from '../../reducers/peachPayOrderReducer';

export default async function initStripePaymentIntegration(orderService: OrderService) {
	const publicKey = Feature.metadata<string>(FeatureFlag.STRIPE_GATEWAYS, 'public_key');
	const connectId = Feature.metadata<string>(FeatureFlag.STRIPE_GATEWAYS, 'connect_id');

	if (!publicKey || !connectId) {
		return;
	}

	const stripe = await loadStripe(publicKey, {stripeAccount: connectId, locale: 'auto'});
	if (!stripe) {
		return;
	}

	const elements = stripe.elements();

	const context: IStripeContext = {
		stripe,
		elements,
	};

	// Internal Payment Methods
	const gateways = registerStripeGateways();
	await activateStripeGateways(context, gateways, orderService);
	setupStripeButton();
}

/**
 * Sets up each stripe payment gateway
 */
function registerStripeGateways(): Record<string, StripeGateway> {
	const gatewayImplementations: Record<string, StripeGateway> = {};
	const gatewayConfigurations: Record<string, GatewayConfiguration> = {};

	if (Feature.enabled(FeatureFlag.STRIPE_CARD_GATEWAY)) {
		const method = initStripeCardMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	if (Feature.enabled(FeatureFlag.STRIPE_AFFIRM_GATEWAY)) {
		const method = initStripeAffirmMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	if (Feature.enabled(FeatureFlag.STRIPE_KLARNA_GATEWAY)) {
		const method = initStripeKlarnaMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	if (Feature.enabled(FeatureFlag.STRIPE_AFTERPAY_GATEWAY)) {
		const method = initStripeAfterPayMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	if (Feature.enabled(FeatureFlag.STRIPE_SOFORT_GATEWAY)) {
		const method = initStripeSofortMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	if (Feature.enabled(FeatureFlag.STRIPE_ACHDEBIT_GATEWAY)) {
		const method = initStripeACHDebitMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	if (Feature.enabled(FeatureFlag.STRIPE_EPS_GATEWAY)) {
		const method = initStripeEPSMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	if (Feature.enabled(FeatureFlag.STRIPE_P24_GATEWAY)) {
		const method = initStripeP24Method();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	if (Feature.enabled(FeatureFlag.STRIPE_SEPADEBIT_GATEWAY)) {
		const method = initStripeSepaDebitMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	if (Feature.enabled(FeatureFlag.STRIPE_BANCONTACT_GATEWAY)) {
		const method = initStripeBancontactMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	if (Feature.enabled(FeatureFlag.STRIPE_GIROPAY_GATEWAY)) {
		const method = initStripeGiropayMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	if (Feature.enabled(FeatureFlag.STRIPE_IDEAL_GATEWAY)) {
		const method = initStripeIdealMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	if (Feature.enabled(FeatureFlag.STRIPE_APPLEPAY_GATEWAY)) {
		const method = initStripeApplepayMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	if (Feature.enabled(FeatureFlag.STRIPE_GOOGLEPAY_GATEWAY)) {
		const method = initStripeGooglepayMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	store.dispatch(registerGatewayBatch(gatewayConfigurations));

	return gatewayImplementations;
}

async function activateStripeGateways(context: IStripeContext, gateways: Record<string, StripeGateway>, orderService: OrderService) {
	const activations = [];
	for (const [_, method] of Object.entries(gateways)) {
		if (method.init) {
			activations.push(method.init(context));
		}
	}

	await Promise.all(activations);

	const confirm = async (event: MouseEvent) => {
		if (!await PeachPayOrder.reportValidity()) {
			return;
		}

		const $target = event.target as HTMLElement | null;
		const $button = $target?.closest<HTMLButtonElement>('button');
		if (!$button) {
			return;
		}

		const gatewayId = PaymentConfiguration.selectedGateway();
		const gateway = gateways[gatewayId];
		if (!gateway) {
			return;
		}

		return paymentFlow(context, gateway, orderService);
	};

	$qsAll<HTMLElement>('.stripe-btn', $el => {
		$el.addEventListener('click', async event => confirm(event));
	});
}

async function paymentFlow(context: IStripeContext, gateway: StripeGateway, orderService: OrderService): Promise<void> {
	store.dispatch(startModalProcessing());

	const [paymentId, update] = await gateway.tokenize(context);

	if (!paymentId && update.note) {
		displayPaymentErrorMessage(update.note ?? '');
	}

	const {error: transactionError, result: transaction} = await orderService.startTransaction(gateway.config.gatewayId);
	if (transactionError || !transaction) {
		const errorMessage = transactionError ? getErrorString(transactionError) : getLocaleText('An unknown error occured while starting the transaction. Please refresh the page and try again.');
		displayPaymentErrorMessage(errorMessage);

		store.dispatch(stopModalLoading());
		return;
	}

	if (!paymentId) {
		await transaction.complete(update);
		store.dispatch(stopModalLoading());
		return;
	}

	const {error: orderError, result: orderResult} = await orderService.placeOrder(transaction, {
		peachpay_stripe_payment_method_id: paymentId,
	});

	if (orderError || !orderResult || orderResult.result !== 'success') {
		store.dispatch(stopModalLoading());
		return;
	}

	const dataURL = new URL(orderResult.redirect);

	if (dataURL.hash === '') {
		await transaction.complete();

		// @ts-expect-error: window.top is always existent
		window.top.location.href = dataURL;
	}

	const [name, data] = dataURL.hash.split('=');
	if (name !== '#payment_data' || !data) {
		await transaction.complete();

		// Any failure after an order risks invalid nonces in the case of a user being logged in. To prevent
		// possible problems we will get a new cart calculation.
		await requestCartCalculation();

		store.dispatch(stopModalLoading());
		return;
	}

	const paymentData = JSON.parse(atob(decodeURIComponent(data))) as PaymentData;

	const clientSecret = paymentData.data.client_secret;
	const intermediateURL = paymentData.intermediate_url;
	const successURL = paymentData.success_url;

	try {
		const [status, update] = await gateway.confirm(context, clientSecret, intermediateURL);

		if (status !== 'success') {
			if (update.note) {
				displayPaymentErrorMessage(update.note ?? '');
			}

			await transaction.complete(update);
			// Any failure after an order risks invalid nonces in the case of a user being logged in. To prevent
			// possible problems we will get a new cart calculation.
			await requestCartCalculation();

			store.dispatch(stopModalLoading());
			return;
		}

		// @ts-expect-error: window.top is always existent
		window.top.location.href = successURL;
	} catch (error) {
		if (error instanceof Error) {
			await transaction.complete({
				note: error.message,
				paymentStatus: 'failed',
			});
		}

		// Any failure after an order risks invalid nonces in the case of a user being logged in. To prevent
		// possible problems we will get a new cart calculation.
		await requestCartCalculation();

		store.dispatch(stopModalLoading());
	}
}
